
export default function ConfirmationEmailPreview() {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
      {/* Section Header */}
      <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-3">
        <div className="w-9 h-9 flex items-center justify-center bg-teal-50 rounded-lg">
          <i className="ri-mail-check-line text-teal-600 text-lg"></i>
        </div>
        <div>
          <h3 className="text-sm font-bold text-gray-900">Confirmation Email Preview</h3>
          <p className="text-xs text-gray-500">What you'll receive after submitting</p>
        </div>
      </div>

      {/* Email Client Chrome */}
      <div className="p-5">
        <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">

          {/* Email Header Bar */}
          <div className="bg-gray-50 border-b border-gray-200 px-5 py-3 space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-semibold text-gray-700 w-8">From</span>
              <span className="bg-teal-50 text-teal-700 border border-teal-100 rounded-full px-2.5 py-0.5 font-medium">
                events@roseville.org
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-semibold text-gray-700 w-8">To</span>
              <span className="bg-gray-100 text-gray-600 rounded-full px-2.5 py-0.5">
                you@youremail.com
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <span className="font-semibold text-gray-700 w-8">Re</span>
              <span className="text-gray-800 font-medium">
                ✅ Event Submission Received — Roseville Community Calendar
              </span>
            </div>
          </div>

          {/* Email Body */}
          <div className="bg-white">

            {/* Email Top Banner */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-500 px-8 py-7 text-center">
              <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-1.5 mb-4">
                <i className="ri-map-pin-2-line text-white text-sm"></i>
                <span className="text-white text-xs font-semibold tracking-wide uppercase">Roseville.org</span>
              </div>
              <h1 className="text-white text-2xl font-bold mb-1">We Got Your Event! 🎉</h1>
              <p className="text-teal-100 text-sm">Your submission is under review</p>
            </div>

            {/* Body Content */}
            <div className="px-8 py-7 space-y-6">

              {/* Greeting */}
              <div>
                <p className="text-gray-800 text-sm leading-relaxed">
                  Hi <strong>[Organizer Name]</strong>,
                </p>
                <p className="text-gray-600 text-sm leading-relaxed mt-2">
                  Thank you for submitting your event to the <strong>Roseville Community Calendar</strong>. We've received your submission and our team will review it within <strong>24 hours</strong>.
                </p>
              </div>

              {/* Submission Summary Card */}
              <div className="bg-gray-50 rounded-xl border border-gray-100 overflow-hidden">
                <div className="px-5 py-3 bg-teal-600 flex items-center gap-2">
                  <i className="ri-calendar-event-line text-white text-sm"></i>
                  <span className="text-white text-xs font-bold uppercase tracking-wide">Submission Summary</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {[
                    { icon: 'ri-calendar-line', label: 'Event Name', value: '[Your Event Name]' },
                    { icon: 'ri-price-tag-3-line', label: 'Category', value: '[Selected Category]' },
                    { icon: 'ri-calendar-2-line', label: 'Date', value: '[Event Date]' },
                    { icon: 'ri-time-line', label: 'Time', value: '[Start Time] – [End Time]' },
                    { icon: 'ri-map-pin-line', label: 'Venue', value: '[Venue Name], Roseville, CA' },
                    { icon: 'ri-money-dollar-circle-line', label: 'Admission', value: '[Admission Type]' },
                  ].map(({ icon, label, value }) => (
                    <div key={label} className="flex items-center gap-3 px-5 py-3">
                      <div className="w-7 h-7 flex items-center justify-center text-teal-500 shrink-0">
                        <i className={`${icon} text-base`}></i>
                      </div>
                      <span className="text-xs text-gray-500 w-24 shrink-0">{label}</span>
                      <span className="text-xs text-gray-800 font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* What Happens Next */}
              <div>
                <h2 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                  <i className="ri-arrow-right-circle-line text-teal-500"></i>
                  What Happens Next
                </h2>
                <div className="space-y-3">
                  {[
                    {
                      step: '1',
                      title: 'Review (within 24 hrs)',
                      desc: 'Our editorial team checks your event for completeness and community guidelines.',
                      color: 'bg-teal-600',
                    },
                    {
                      step: '2',
                      title: 'Approval Email',
                      desc: 'You\'ll receive a second email confirming your event is live on the calendar.',
                      color: 'bg-teal-500',
                    },
                    {
                      step: '3',
                      title: 'Go Live',
                      desc: 'Your event appears on the Roseville Community Calendar and is visible to thousands of residents.',
                      color: 'bg-teal-400',
                    },
                  ].map(({ step, title, desc, color }) => (
                    <div key={step} className="flex gap-3">
                      <div className={`w-6 h-6 ${color} text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0 mt-0.5`}>
                        {step}
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-gray-800">{title}</p>
                        <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Promote CTA */}
              <div className="bg-amber-50 border border-amber-100 rounded-xl px-5 py-4 flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-amber-100 rounded-lg shrink-0">
                  <i className="ri-rocket-line text-amber-600 text-base"></i>
                </div>
                <div>
                  <p className="text-xs font-bold text-amber-800 mb-0.5">Want more visibility?</p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Upgrade to a <strong>Featured</strong> or <strong>Promoted</strong> listing to reach even more Roseville residents — appear at the top of the calendar and in our weekly newsletter.
                  </p>
                  <div className="mt-2.5">
                    <span className="inline-block bg-amber-500 text-white text-xs font-semibold px-3 py-1.5 rounded-lg cursor-pointer">
                      Promote My Event →
                    </span>
                  </div>
                </div>
              </div>

              {/* Need Help */}
              <div className="border-t border-gray-100 pt-5">
                <p className="text-xs text-gray-500 leading-relaxed">
                  Need to make changes or have questions? Reply to this email or contact us at{' '}
                  <span className="text-teal-600 font-medium">events@roseville.org</span>.
                  We typically respond within a few hours during business hours.
                </p>
              </div>

              {/* Sign-off */}
              <div>
                <p className="text-xs text-gray-600">
                  Warm regards,<br />
                  <strong className="text-gray-800">The Roseville.org Events Team</strong>
                </p>
              </div>
            </div>

            {/* Email Footer */}
            <div className="bg-gray-50 border-t border-gray-100 px-8 py-5 text-center space-y-2">
              <div className="flex items-center justify-center gap-4">
                {['ri-facebook-fill', 'ri-instagram-line', 'ri-twitter-x-line'].map(icon => (
                  <div key={icon} className="w-7 h-7 flex items-center justify-center bg-gray-200 rounded-full cursor-pointer hover:bg-teal-100 transition-colors">
                    <i className={`${icon} text-gray-500 text-xs`}></i>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400">
                © 2025 Roseville.org · Your Roseville Community Hub
              </p>
              <p className="text-xs text-gray-400">
                <span className="underline cursor-pointer hover:text-gray-600">Unsubscribe</span>
                {' · '}
                <span className="underline cursor-pointer hover:text-gray-600">Privacy Policy</span>
                {' · '}
                <span className="underline cursor-pointer hover:text-gray-600">View in browser</span>
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
